/**
 * Create a mutex to
 * wait until refreshToken flow successes if refresh toke is busy previously make the call
 *
 */
import { Mutex } from 'async-mutex';

const LOCK_STORAGE_KEY = 'st-cloud-auth-refresh-lock-v1';
const LOCK_TTL_MS = 15000;
const ACQUIRE_TIMEOUT_MS = 30000;
const POLL_INTERVAL_MS = 100;

interface StoredLock {
  owner: string;
  expiresAt: number;
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

class AuthMutex {
  private readonly localMutex = new Mutex();

  private readonly ownerId = `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

  private heartbeatTimer?: ReturnType<typeof setInterval>;

  private readonly isBrowser =
    globalThis.window !== undefined &&
    globalThis.window.localStorage !== undefined;

  private readStoredLock(): StoredLock | null {
    if (!this.isBrowser) return null;

    try {
      const value = globalThis.window.localStorage.getItem(LOCK_STORAGE_KEY);
      if (!value) return null;
      const parsed = JSON.parse(value) as StoredLock;
      if (!parsed?.owner || !parsed?.expiresAt) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private removeStoredLockIfExpired(lock: StoredLock | null): boolean {
    if (!this.isBrowser || !lock) return false;
    if (lock.expiresAt > Date.now()) return false;

    try {
      globalThis.window.localStorage.removeItem(LOCK_STORAGE_KEY);
    } catch {
      // ignore storage errors and fallback to in-tab mutex only
    }

    return true;
  }

  private isCrossTabLockedByOtherTab() {
    const lock = this.readStoredLock();
    if (!lock) return false;
    if (this.removeStoredLockIfExpired(lock)) return false;
    return lock.owner !== this.ownerId;
  }

  private tryClaimCrossTabLock(): boolean {
    if (!this.isBrowser) return true;

    const currentLock = this.readStoredLock();
    if (
      currentLock &&
      currentLock.owner !== this.ownerId &&
      currentLock.expiresAt > Date.now()
    ) {
      return false;
    }

    const lock: StoredLock = {
      owner: this.ownerId,
      expiresAt: Date.now() + LOCK_TTL_MS,
    };

    try {
      globalThis.window.localStorage.setItem(
        LOCK_STORAGE_KEY,
        JSON.stringify(lock)
      );
      const lockAfterSet = this.readStoredLock();
      return lockAfterSet?.owner === this.ownerId;
    } catch {
      // localStorage unavailable (privacy mode / blocked), fallback to local mutex
      return true;
    }
  }

  private startHeartbeat() {
    if (!this.isBrowser) return;
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const lock: StoredLock = {
        owner: this.ownerId,
        expiresAt: Date.now() + LOCK_TTL_MS,
      };
      try {
        globalThis.window.localStorage.setItem(
          LOCK_STORAGE_KEY,
          JSON.stringify(lock)
        );
      } catch {
        // ignore storage errors and keep local mutex semantics
      }
    }, Math.max(1000, Math.floor(LOCK_TTL_MS / 3)));
  }

  private stopHeartbeat() {
    if (!this.heartbeatTimer) return;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
  }

  private async acquireCrossTabLock(): Promise<() => void> {
    if (!this.isBrowser) return () => undefined;

    const startedAt = Date.now();
    while (Date.now() - startedAt < ACQUIRE_TIMEOUT_MS) {
      if (this.tryClaimCrossTabLock()) {
        this.startHeartbeat();
        return () => {
          this.stopHeartbeat();
          try {
            const lock = this.readStoredLock();
            if (lock?.owner === this.ownerId) {
              globalThis.window.localStorage.removeItem(LOCK_STORAGE_KEY);
            }
          } catch {
            // ignore storage errors on release
          }
        };
      }
      await sleep(POLL_INTERVAL_MS);
    }

    throw new Error('Auth mutex timeout while acquiring cross-tab lock');
  }

  isLocked = () =>
    this.localMutex.isLocked() || this.isCrossTabLockedByOtherTab();

  waitForUnlock = async () => {
    await this.localMutex.waitForUnlock();

    if (!this.isBrowser) return;

    const startedAt = Date.now();
    while (this.isCrossTabLockedByOtherTab()) {
      if (Date.now() - startedAt >= ACQUIRE_TIMEOUT_MS)
        throw new Error('Auth mutex timeout while waiting unlock');
      await sleep(POLL_INTERVAL_MS);
    }
  };

  runExclusive = async <T>(callback: () => Promise<T> | T): Promise<T> => {
    const releaseLocal = await this.localMutex.acquire();
    let releaseCrossTab: () => void = () => undefined;

    try {
      releaseCrossTab = await this.acquireCrossTabLock();
      return await callback();
    } finally {
      releaseCrossTab();
      releaseLocal();
    }
  };
}

const authMutex = new AuthMutex();

export default authMutex;
